import { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";

import {
  requestAvatarGeneration,
  fetchAvatarGenerationStatus,
} from "../../services/api";

type AvatarToolsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentAvatarUrl?: string | null;
  onAvatarUpdated?: (url: string) => void;
  onGenerationStart?: () => void;
  onGenerationEnd?: () => void;
};

export function AvatarToolsModal({
  isOpen,
  onClose,
  currentAvatarUrl,
  onAvatarUpdated,
  onGenerationStart,
  onGenerationEnd,
}: AvatarToolsModalProps) {
  const toast = useToast();
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState("idle");
  const [notice, setNotice] = useState("");
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const surface = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const helperColor = useColorModeValue("gray.500", "whiteAlpha.700");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const hasAvatar = Boolean(currentAvatarUrl && !currentAvatarUrl.includes("/avatar/default"));
  const handleGenerate = async () => {
    if (submitting.current || !description.trim()) return;
    submitting.current = true;
    setIsSubmitting(true);
    setPhase("preparing");
    setNotice("");
    onGenerationStart?.();
    try {
      const payload = { description, request_id: crypto.randomUUID() };
      const res = await requestAvatarGeneration(payload);
      if (res.status === "clarification") {
        setPhase("clarification");
        setNotice(res.clarification || "Please describe the change more specifically.");
        return;
      }
      let candidate: string | null =
        res.avatar_url || res.url || res.image || res.image_url || null;

      if (!candidate && res.status === "running" && res.prompt_id) {
        setPhase("generating");
        candidate = await pollGenerationStatus(res.prompt_id);
      }


      if (!candidate) {
        throw new Error(res.error || "No avatar URL returned");
      }

      if (!alive.current) return;
      onAvatarUpdated?.(candidate);
      setPhase("success");
      setNotice("Your avatar is saved to your account.");
      toast({
        status: "success",
        title: "Avatar saved",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      setPhase("error");
      setNotice("Could not finish your avatar. Please try again. Your saved avatar is unchanged.");
      toast({
        status: "error",
        title: "Avatar generation failed",
        description: "Please try again.",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      submitting.current = false;
      if (alive.current) setIsSubmitting(false);
      onGenerationEnd?.();
    }
  };

  const pollGenerationStatus = async (promptId: string): Promise<string | null> => {
    const start = Date.now();
    const timeoutMs = 1_210_000;
    let lastError: unknown = null;
    while (alive.current && Date.now() - start < timeoutMs) {
      try {
        const status = await fetchAvatarGenerationStatus(promptId);
        if (status.status === "done") {
          const url = status.avatar_url || status.url || status.image || status.image_url || "";
          if (url) return url;
        }
        if (status.status === "error") {
          lastError = new Error(status.error || "generation error");
          break;
        }
      } catch (err) {
        lastError = err;
      }
      await new Promise((resolve) => setTimeout(resolve, 4000));
    }
    if (lastError) {
      console.warn("avatar status polling ended with error", lastError);
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.700"
      zIndex={2100}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={{ base: 4, md: 6 }}
      pt="calc(var(--safe-top) + 12px)"
      pb="calc(var(--safe-bottom) + 12px)"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <Box
        w="100%"
        maxW="1100px"
        maxH="calc(var(--app-dvh) - var(--safe-top) - var(--safe-bottom) - 24px)"
        bg={surface}
        color={textColor}
        p={{ base: 5, md: 7 }}
        borderRadius="2xl"
        boxShadow="2xl"
        position="relative"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        onClick={(event) => event.stopPropagation()}
      >
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-br, rgba(255,105,180,0.15), rgba(118,75,255,0.12))"
          pointerEvents="none"
        />
        <Box position="relative" zIndex={1} display="flex" flexDirection="column" flex="1" minH="0">
          <HStack justify="space-between" mb={4} flexShrink={0}>
            <Box>
              <Text fontWeight="bold" fontSize="xl">
                Avatar tools
              </Text>
              <Text fontSize="sm" color={helperColor}>
                Describe the look you want, then generate when you’re ready.
              </Text>
            </Box>
            <IconButton
              aria-label="Close avatar tools"
              icon={<CloseIcon boxSize={3} />}
              size="lg"
              minW={11}
              minH={11}
              variant="ghost"
              onClick={onClose}
            />
          </HStack>

          <Box
            flex="1"
            minH="0"
            overflowY="auto"
            pb="calc(var(--safe-bottom) + 12px)"
            css={{ WebkitOverflowScrolling: "touch" }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <VStack align="stretch" spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="avatar-description" fontSize="sm">
                    {hasAvatar ? "What would you like to change?" : "Describe your avatar"}
                  </FormLabel>
                  <Textarea
                    id="avatar-description"
                    size="lg"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Same face, oversized cream sweater, sitting by a rainy window, warm evening light."
                    minH="160px"
                    maxLength={2000}
                    isDisabled={isSubmitting}
                    resize="vertical"
                  />
                </FormControl>
                <Text fontSize="sm" color={helperColor}>
                  Your saved description guides each new image. Facial details may vary.
                  You can close this panel and keep chatting while it generates.
                </Text>
              </VStack>

              <VStack align="stretch" spacing={4}>
                <Box
                  borderWidth="1px"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="sm"
                  bg="blackAlpha.50"
                >
                  {currentAvatarUrl ? (
                    <Image
                      src={currentAvatarUrl}
                      alt="Current avatar"
                      w="100%"
                      h="100%"
                      loading="lazy"
                      decoding="async"
                      objectFit="cover"
                      aspectRatio="3 / 4"
                      sizes="(max-width: 768px) 100vw, 320px"
                    />
                  ) : (
                    <Box p={6}>
                      <Text color={helperColor} textAlign="center">
                        Your current avatar will show here.
                      </Text>
                    </Box>
                  )}
                </Box>
                <Box>
                  <VStack align="stretch" spacing={3}>
                    <Button
                      colorScheme="pink"
                      size="lg"
                      onClick={() => void handleGenerate()}
                      isDisabled={isSubmitting || !description.trim()}
                    >
                      {isSubmitting ? <Spinner size="sm" mr={2} /> : null}
                      {isSubmitting ? (phase === "preparing" ? "Preparing…" : "Generating…") : (hasAvatar ? "Update Avatar" : "Generate Avatar")}
                    </Button>
                    <Text fontSize="xs" color={helperColor} ml={1}>
                      Saved to your account when ready.
                    </Text>
                  </VStack>
                </Box>

                {notice && <Text role={phase === "error" ? "alert" : "status"}>{notice}</Text>}
                {isSubmitting && (
                  <HStack spacing={2} color={helperColor}>
                    <Spinner size="sm" color="pink.400" />
                    <Text fontSize="sm">{phase === "preparing" ? "Preparing your avatar…" : "Generating your avatar…"}</Text>
                  </HStack>
                )}
              </VStack>
            </SimpleGrid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AvatarToolsModal;
